const SOFT_ISSUES = new Set(['FULL_NAME_NOT_EXACT', 'BIRTH_DATE_REQUIRED_FOR_NAME_MATCH',
    'SOURCE_DEPARTMENT_UNMAPPED', 'CANONICAL_FIELDS_WILL_BE_REPLACED']);
const comparable = value => String(value || '').trim().toLowerCase().replace(/ё/g, 'е')
    .replace(/[^a-zа-я0-9]+/gi, ' ').replace(/\s+/g, ' ').trim();
const employeeId = value => Number.isInteger(Number(value)) && Number(value) > 0 ? Number(value) : null;

function person(row) {
    const data = row.canonical || {};
    return {name: ['last_name', 'first_name', 'middle_name'].map(key => comparable(data[key])).join('|'),
        birth: String(data.birth_date || '').slice(0, 10)};
}

function samePerson(people) {
    return new Set(people.map(item => item.name)).size === 1
        && new Set(people.map(item => item.birth).filter(Boolean)).size <= 1;
}

function subjectKey(row) {
    const organization = comparable((row.canonical || {}).organization_name);
    const personnel = String((row.employment || {}).personnel_number || '').trim().toLowerCase();
    return organization && personnel ? JSON.stringify([organization, personnel]) : null;
}

function singleCompatibleCandidate(row, decision) {
    if (row.status !== 'conflict' || row.manual_termination_event_id
        || (row.issues || []).some(issue => !SOFT_ISSUES.has(issue.code))) return null;
    const rejected = new Set((decision.reject_candidate_ids || []).map(Number));
    const candidates = new Map();
    (row.match_candidates || row.candidates || []).forEach(candidate => {
        const id = employeeId(candidate.employee_id);
        if (id && candidate.match_decision !== 'reject' && !rejected.has(id)) candidates.set(id, candidate);
    });
    if (candidates.size !== 1) return null;
    const candidate = Array.from(candidates.values())[0];
    const states = {};
    (candidate.match_evidence || []).forEach(item => { states[item.field] = item.state; });
    // This is a batch of explicit choices, not a new automatic identity rule.
    // Missing DOB is disclosed in the confirmation; typos/contradictions stay manual.
    return states.last_name === 'exact'
        && ['exact', 'initial'].includes(states.first_name)
        && ['exact', 'initial'].includes(states.middle_name)
        && ['exact', 'missing'].includes(states.birth_date)
        ? employeeId(candidate.employee_id) : null;
}

export function planOneCBulkMatches(rows, decisions) {
    const groups = new Map();
    const targets = new Map();
    const choices = decisions || {};
    const addTarget = (id, row) => {
        if (!id) return;
        if (!targets.has(id)) targets.set(id, []);
        targets.get(id).push(person(row));
    };
    (rows || []).forEach(row => {
        const decision = choices[row.decision_key] || {};
        const target = decision.action === 'match' ? employeeId(decision.employee_id)
            : !decision.action && row.status === 'matched' ? employeeId((row.matched_employee || {}).employee_id) : null;
        addTarget(target, row);
        const key = subjectKey(row);
        if (!key || row.status === 'duplicate_source' || decision.action === 'ignore') return;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push({row, decision, target});
    });
    const plans = [];
    groups.forEach(entries => {
        if (!samePerson(entries.map(entry => person(entry.row)))) return;
        const items = [];
        const ids = new Set();
        for (const {row, decision, target} of entries) {
            if (decision.action || row.status === 'matched') {
                // Preserve deliberate create/ignore/match choices, including other contracts.
                if (!target) return;
                ids.add(target);
                continue;
            }
            const id = singleCompatibleCandidate(row, decision);
            if (!id) return;
            ids.add(id);
            items.push({decision_key: row.decision_key, employee_id: id});
        }
        if (ids.size !== 1 || !items.length) return;
        const id = Array.from(ids)[0];
        plans.push({id, items});
        entries.forEach(entry => addTarget(id, entry.row));
    });
    // An initials-only card may be the sole candidate for two different people.
    // Do not batch either choice, even when they have different personnel codes.
    const accepted = plans.filter(plan => samePerson(targets.get(plan.id)));
    const items = [].concat(...accepted.map(plan => plan.items));
    return {items, row_count: items.length, people_count: new Set(accepted.map(plan => plan.id)).size};
}
